import { Injectable } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { ConfigService } from '@app/shared/services/config.service'
import { AuthService } from '@core/auth.service'
import { CodeService } from '@core/code.service'
import { NavigatorService } from '@core/navigator.service'
import { NotifyService } from '@core/notify.service'
import { StageService } from '@core/stage.service'
import { StoreService } from '@core/store.service'
import { TimeService } from '@core/time.service'
import { UserService } from '@core/user.service'

@Injectable()
export class CoreService {
   console: boolean = false
   logPrefix: string = '@core - ' + this.constructor.name

   public modeDevel: boolean = false

   public appData: any

   public dialogRef: any = null

   constructor(
      public code: CodeService,
      public store: StoreService,
      public user: UserService,
      public auth: AuthService,
      public nav: NavigatorService,
      public stage: StageService,
      public notify: NotifyService,
      public time: TimeService,
      public dialog: MatDialog,
      public config: ConfigService
   ) {
      if (!this.isProduction()) {
         this.enableModeDevel()
      }
   }

   setup() {
      if (this.console) console.log(this.logPrefix, 'setup')

      this.store.setData('appPages', {})
      this.store.setData('appMenuItems', {})
      this.store.setData('appMenus', {})

      this.appData = {
         running: true,
         currentLayout: null,
         currentPageName: null,
      }
   }

   isProduction() {
      return this.config.environment === 'prod'
   }

   enableModeDevel() {
      this.modeDevel = true
      this.console = true
      this.auth.console = true
      this.nav.console = true
      this.stage.console = true
   }

   disableModeDevel() {
      this.modeDevel = false
      this.console = false
      this.auth.console = false
      this.nav.console = false
      this.stage.console = false
   }

   spinnerShow() {
      this.stage.spinnerShow()
   }

   spinnerHide() {
      this.stage.spinnerHide()
   }

   setPage(name: string, data: any) {
      if (this.console) console.log(this.logPrefix, 'setPage:', name)

      if (name && this.code.isObject(data) && !this.code.isEmpty(data)) {
         if (data.hasOwnProperty('route') && data.route) {
            // required

            let pageData = {
               name: name,
               route: data.route,
               label: data.hasOwnProperty('label') ? data.label : 'undefined',
               icon: null,
               params: data.hasOwnProperty('params') ? data.params : {},
            }

            if (data.hasOwnProperty('iconName')) {
               const icon = this.getIcon(data.iconName)

               if (icon) pageData['icon'] = icon
            }

            var storeReply = this.store.setData('appPages/' + name, pageData)
            if (!storeReply.error) {
               return true
            }
         }
      }
      return false
   }

   getPage(name: string) {
      //if(this.console) console.log( this.logPrefix,  'getPage - name:', name);

      if (name) {
         var storeReply = this.store.getData('appPages/' + name)
         if (!storeReply.error) {
            return storeReply.result
         }
      }
      return null
   }

   setPages(itemslist: any) {
      if (this.console) console.log(this.logPrefix, 'setPages:', itemslist)

      if (itemslist && this.code.isObject(itemslist) && !this.code.isEmpty(itemslist)) {
         let count = 0

         for (let itemName in itemslist) {
            if (this.setPage(itemName, itemslist[itemName])) count++
         }

         if (count == Object.keys(itemslist).length) {
            return true
         }
      }
      return false
   }

   getPages() {
      //if(this.console) console.log( this.logPrefix,  'getPages');

      var storeReply = this.store.getData('appPages')
      if (!storeReply.error) {
         return storeReply.result
      }
      return null
   }

   getCurrentPage() {
      if (this.console) console.log(this.logPrefix, 'getCurrentPage')

      return this.getPage(this.appData.currentPageName)
   }

   getPageByRoute(route: string) {
      if (this.console) console.log(this.logPrefix, 'getPageByRoute - route:', route)

      if (route) {
         var storeReply = this.store.getData('appPages')

         const pages = !storeReply.error ? storeReply.result : {}

         if (!this.code.isEmpty(pages)) {
            if (this.console) console.log(this.logPrefix, 'getPageByRoute - pages:', pages)

            let routeSplit = route.split('/')

            if (this.console) console.log(this.logPrefix, 'getPageByRoute - routeSplit:', routeSplit)

            if (routeSplit.length) {
               for (var i = 0; i <= routeSplit.length + 1; i++) {
                  //if(this.console) console.log( this.logPrefix,  'getPageByRoute - loop:', i, routeSplit.length, routeSplit);

                  let routePart = routeSplit.join('/')
                  if (!routePart) routePart = '/'

                  if (this.console) console.log(this.logPrefix, 'getPageByRoute - routePart:', routePart)

                  for (let name in pages) {
                     let page = pages[name]

                     if (this.console) console.log(this.logPrefix, 'getPageByRoute - check:', page['route'])

                     if (page['route'] == routePart) {
                        return page
                     }
                  }

                  routeSplit.pop() //remove last
               }
            }
         }
      }

      return null
   }

   goToPage(name: string) {
      if (this.console) console.log(this.logPrefix, 'goToPage', name)

      if (name) {
         const page = this.getPage(name)

         if (page) this.nav.goToRoute(page.route)
      }
   }

   //-- Icons --//

   setIcons(icons: any) {
      if (icons && this.code.isObject(icons) && !this.code.isEmpty(icons)) {
         var storeReply = this.store.setData('appIcons', icons)

         if (!storeReply.error) {
            return true
         }
      }
      return false
   }

   getIcon(name: string) {
      //if(this.console) console.log( this.logPrefix, 'getIcon - name:', name);

      if (name) {
         var storeReply = this.store.getData('appIcons/' + name)

         if (!storeReply.error) {
            return storeReply.result
         }
      }

      return null
   }

   //-- Menus --//

   setMenuItems(items: any) {
      if (items && this.code.isObject(items) && !this.code.isEmpty(items)) {
         var storeReply = this.store.setData('appMenuItems', items)

         if (!storeReply.error) {
            return true
         }
      }
      return false
   }

   setMenu(menuName: string, itemsList: string[]) {
      //if (this.console) console.log(this.logPrefix, 'setMenu');

      let menuData = []

      if (menuName && itemsList && this.code.isArray(itemsList) && itemsList.length) {
         //if (this.console) console.log(this.logPrefix, 'setMenu - itemsList:', itemsList);

         var storeReply = this.store.getData('appMenuItems')

         const menuItems = !storeReply.error ? storeReply.result : {}

         //if (this.console) console.log(this.logPrefix, 'setMenu - menuItems:', menuItems);

         if (!this.code.isEmpty(menuItems)) {
            for (let itemName of itemsList) {
               if (menuItems.hasOwnProperty(itemName)) {
                  let menuItem = menuItems[itemName]

                  if (menuItem.hasOwnProperty('type') && menuItem.type) {
                     let confirm = false

                     switch (menuItem.type) {
                        case 'page':
                           const page = this.getPage(menuItem.params.pageName)

                           if (page) {
                              menuItem['label'] = menuItem.params.hasOwnProperty('label') ? menuItem.params.label : page.label

                              menuItem['route'] = page.route

                              confirm = true

                              if (page.icon) menuItem['icon'] = page.icon
                           }

                           break
                        case 'submenu':
                           const submenuRef = 'submenu_' + menuName + '_' + itemName

                           const subItemslist = menuItem.params.list || []

                           if (this.code.isArray(subItemslist) && subItemslist.length) {
                              let submenuData = []

                              for (let subItemName of subItemslist) {
                                 if (menuItems.hasOwnProperty(subItemName)) {
                                    let submenuItem = menuItems[subItemName]

                                    if (submenuItem.hasOwnProperty('type') && submenuItem.type == 'page') {
                                       const page = this.getPage(submenuItem.params.pageName)

                                       if (page) {
                                          submenuItem['label'] = submenuItem.params.hasOwnProperty('label') ? submenuItem.params.label : page.label

                                          submenuItem['route'] = page.route

                                          if (page.icon) submenuItem['icon'] = page.icon

                                          submenuData.push(submenuItem)
                                       }
                                    }
                                 }
                              }

                              if (submenuData.length) {
                                 menuItem['label'] = menuItem.params.hasOwnProperty('label') ? menuItem.params.label : null

                                 menuItem['data'] = submenuData

                                 menuItem['id'] = submenuRef

                                 confirm = true

                                 if (menuItem.params.hasOwnProperty('iconName')) {
                                    const icon = this.getIcon(menuItem.params.iconName)

                                    if (icon) menuItem['icon'] = icon
                                 }
                              }
                           }

                           break
                     }

                     if (confirm) menuData.push(menuItem)
                  }
               }
            }
         }

         //if (this.console) console.log(this.logPrefix, 'setMenu - RESULT:', 'appMenus/'+menuName, menuData);

         var storeReply = this.store.setData('appMenus/' + menuName, menuData)

         if (!storeReply.error) {
            return true
         }
      }

      return false
   }

   getMenu(menuName: string) {
      if (this.console) console.log(this.logPrefix, 'getMenu')

      let appMenu = {
         data: [],
         submenu: null,
      }

      if (menuName) {
         var storeReply = this.store.getData('appMenus/' + menuName)

         if (!storeReply.error) {
            appMenu.data = storeReply.result

            for (let menuIndex in appMenu.data) {
               let menuItem = appMenu.data[menuIndex]

               if (menuItem.type == 'submenu') {
                  for (let submenuIndex in menuItem.data) {
                     let submenuItem = menuItem.data[submenuIndex]

                     if (submenuItem.route == this.nav.currentRoute) {
                        appMenu.submenu = menuItem.id
                     }
                  }
               }
            }
         }
      }

      return appMenu
   }
}

